import { idiv, imax, imin, withinRange } from '../math/fixed.ts';
import type { BattleState, Bot } from '../types.ts';
import { ev } from './combat.ts';

/**
 * The Order's bell (El Párpado 2, docs/SAGAS.md). First the climb: an escort whose caravan is the fallen bell, dragged
 * up the slope on ropes by the heroes beside it (more hands, quicker; Ulla's stone boars pull too, if the group did El
 * Cuerno). It doesn't stop for the nightmares: their blows cut its ropes, each rope its share of the bell's life, and
 * every rope cut lets it slide back down the road a way; all of them cut, it rolls down the slope (the escort lost).
 * Each of the titan's shakes (battle/neck.ts) makes it slip back too, unless a hero beside it stands still to brace it.
 *
 * Then hanging it: three winches round the frame (the node) wind it up as long as a hero stands at them, each one
 * turned adding its turn; with none turned it sinks back. Wound all the way up wins it; the frame falling loses it.
 */

const bell = (s: BattleState): Bot | undefined => (s.escort ? s.bots?.find((b) => b.id === s.escort?.cart && b.hp > 0) : undefined);

/** Ropes still whole for a bell's life: each one is its share of it (rounded up: the last goes with the last blow). */
const whole = (b: Bot, ropes: number): number => (b.hp <= 0 ? 0 : imin(ropes, idiv(b.hp * ropes + b.maxHp - 1, imax(1, b.maxHp))));

export const climbTick = (s: BattleState): void => {
  const r = s.rules?.climb;
  const e = s.escort;
  if (!r || !e || !s.started || e.arrived) return;
  const st = (s.climb ??= { ropes: r.ropes, to: -1 });
  const b = s.bots?.find((x) => x.id === e.cart);
  if (!b) return;
  const ropes = whole(b, r.ropes);
  if (ropes < st.ropes) {
    // A rope cut (or more, with one blow): it slides back down its road. All of them: down the slope.
    for (let i = ropes; i < st.ropes; i++) ev(s, { t: 'hoist', kind: ropes ? 'cut' : 'roll', i, x: b.x, y: b.y });
    if (ropes) slideBack(s, (st.ropes - ropes) * r.slip);
    st.ropes = ropes;
  }
  if (!ropes) return;
  // Done sliding once it is back on its way up.
  if (st.to >= 0 && e.next > st.to) st.to = -1;
  if (st.to >= 0) {
    e.speed = r.slide;
    return;
  }
  // Dragged by the heroes beside it (and the boars), slower with ropes cut.
  const hands = s.heroes.filter((h) => h.downTicks === 0 && withinRange(h.x, h.y, b.x, b.y, r.pull)).length + r.boars;
  const pace = r.pace[imin(hands, r.pace.length - 1)] ?? 0;
  e.speed = idiv(pace * ropes, r.ropes);
};

/** The bell slides `n` points back down its road (not past where it started). */
const slideBack = (s: BattleState, n: number): void => {
  const e = s.escort;
  const st = s.climb;
  if (!e || !st) return;
  const to = imax(0, e.next - 1 - n);
  st.to = to;
  e.next = to;
};

/** A shake on the climb: the bell slips back, unless a hero beside it stands still to brace it. */
export const climbShake = (s: BattleState): void => {
  const r = s.rules?.climb;
  const st = s.climb;
  const b = bell(s);
  if (!r || !st || !b || s.escort?.arrived) return;
  if (s.heroes.some((h) => h.downTicks === 0 && !h.moving && withinRange(h.x, h.y, b.x, b.y, r.brace))) {
    ev(s, { t: 'hoist', kind: 'brace', i: 0, x: b.x, y: b.y });
    return;
  }
  ev(s, { t: 'hoist', kind: 'slip', i: 0, x: b.x, y: b.y });
  slideBack(s, r.shakeSlip);
};

/** Whether the bell's climb is on (the caravan doesn't stop for the nightmares: they cut its ropes instead). */
export const climbing = (s: BattleState): boolean => !!s.rules?.climb;

export const winchTick = (s: BattleState): void => {
  const r = s.rules?.winch;
  if (!r) return;
  const st = (s.winch ??= { lift: 0, turning: r.at.map(() => false), done: 0 });
  if (!s.started || st.done) return;
  let turned = 0;
  r.at.forEach((w, i) => {
    const on = s.heroes.some((h) => h.downTicks === 0 && withinRange(h.x, h.y, w.x, w.y, r.reach));
    if (on !== st.turning[i]) ev(s, { t: 'hoist', kind: on ? 'turn' : 'stop', i, x: w.x, y: w.y });
    st.turning[i] = on;
    if (on) turned++;
  });
  st.lift = turned ? imin(r.need, st.lift + turned) : imax(0, st.lift - r.sink);
  if (st.lift < r.need) return;
  st.done = s.tick;
  ev(s, { t: 'hoist', kind: 'up', i: -1, x: 0, y: 0 });
};

/** The bell up: won (a moment later); the frame fallen: lost. */
export const winchResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.winch;
  if (!r) return null;
  if (s.keep.hp <= 0) return 'lost';
  return s.winch?.done && s.tick - s.winch.done >= r.beat ? 'won' : null;
};
